import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { signInWithGoogle, signInWithPassword } from '@pc/db';
import { describeError } from '@pc/core';
import { Button, Card, Display, Field, GoogleButton, Input } from '@pc/ui';
import { FooterLanguagePicker } from '@pc/i18n';
import { env } from '../env';

// Hosted in the project's `Public Photos` storage bucket. Same asset every
// other surface (AppShell, AdminShell, app-site sign-in) uses so the auth
// screen stays visually anchored to the brand instead of dropping a text
// wordmark.
const PC_LOGO_URL =
  'https://bxbdqmtldkhgwpnwecox.supabase.co/storage/v1/object/public/Public%20Photos/LOGOUpdated.png';

/**
 * Validate the `?next=` redirect target. We only allow same-origin,
 * absolute-path destinations (`/...`) to prevent open-redirect abuse
 * via a crafted `next=https://evil.example`. `/sign-in` itself is
 * also rejected so a botched query string can't loop the user.
 */
function safeNextPath(raw: string | null): string {
  if (!raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  if (raw === '/sign-in' || raw.startsWith('/sign-in?') || raw.startsWith('/sign-in/'))
    return '/';
  return raw;
}

export function SignInPage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nextPath = safeNextPath(params.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInWithPassword(email, password);
      nav(nextPath, { replace: true });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  // Shop runs Google OAuth on its OWN origin (not through the central
  // auth handoff): shop keeps an isolated `pc-shop.auth` session bucket
  // and is deliberately NOT in the auth site's return_to allow-list, so a
  // central handoff has nowhere to land. supabase-js's detectSessionInUrl
  // consumes the provider response when the browser returns to
  // `redirectTo`, mints the session into `pc-shop.auth`, and the
  // AuthProvider's SIGNED_IN listener records the login audit. We send the
  // user straight to their intended `next` path; any shop route can
  // consume the token because App.tsx runs initSupabase on every route.
  async function onGoogle() {
    setError(null);
    setBusy(true);
    try {
      const redirectTo = `${window.location.origin}${nextPath}`;
      await signInWithGoogle(redirectTo);
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  const signUpHref = (() => {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : '';
    const back = nextPath && nextPath !== '/' ? `${origin}${nextPath}` : origin;
    return `${env.webAppUrl}/sign-in?mode=signup${
      back ? `&next=${encodeURIComponent(back)}` : ''
    }`;
  })();

  return (
    <main className="min-h-screen grid place-items-center bg-paper px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src={PC_LOGO_URL}
            alt={t('auth.signIn.title')}
            width="160"
            height="40"
            className="h-10 w-auto block"
          />
          <Display size="md" className="mt-4">
            <Trans i18nKey="auth.signInShop.heading" components={{ em: <em /> }} />
          </Display>
          <p className="mt-3 text-sm text-muted">{t('auth.signInShop.subtitle')}</p>
        </div>
        <Card>
          <div className="mb-4 flex flex-col gap-4">
            <GoogleButton
              label={t('auth.continueWithGoogle')}
              onClick={() => void onGoogle()}
              disabled={busy}
            />
            <div className="flex items-center gap-3 text-xs text-muted">
              <span className="h-px flex-1 bg-rule" />
              <span>{t('auth.or')}</span>
              <span className="h-px flex-1 bg-rule" />
            </div>
          </div>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Field label={t('auth.signIn.emailLabel')} htmlFor="email">
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label={t('auth.signIn.passwordLabel')} htmlFor="password">
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error ? <p className="text-sm text-clay">{error}</p> : null}
            <Button type="submit" disabled={busy}>
              {busy ? t('auth.signIn.signingIn') : t('actions.signIn')}
            </Button>
          </form>
        </Card>
        <p className="text-xs text-muted text-center mt-4">
          {t('auth.signInShop.noAccount')}{' '}
          <a href={signUpHref} className="underline">
            {t('auth.signInShop.signUpAt', {
              host: env.webAppUrl.replace(/^https?:\/\//, ''),
            })}
          </a>
        </p>
        <div className="mt-8 flex justify-center">
          <FooterLanguagePicker />
        </div>
      </div>
    </main>
  );
}
